// @givmo/board — the director's read of what a meeting recorded, and signing the minutes.
//
// ══ WHAT A DIRECTOR CAN DO HERE, AND WHAT THEY DELIBERATELY CANNOT ═════════════════════════════
//
// **Read** the record of a meeting they were called to, and **sign** approved minutes. That is all,
// and the omission is the design:
//
//   * **THERE IS NO DIRECTOR-SIDE VOTING.** A vote taken in a room is not performed through the
//     module — the secretary writes it down (plan §1.7's manual path). What runs through the signing
//     window is what a director performs THROUGH the module: the consent signature, the
//     acknowledgment, and this minute signature. A remote/electronic vote cast through the portal
//     WOULD be a signature-class act, and is a later item.
//   * **THERE IS NO "I WAS PRESENT" AFFORDANCE.** Attendance is always the recording officer's entry
//     (`recorded_by_user_id` is NOT NULL and there is no director-facing write path) — a director does
//     not self-report having been in a room. That is the exact reverse of the RSVP, where the
//     director's own answer is first-class.
//
// ══ ABSENCE, AGAIN, BECAUSE THIS IS THE SURFACE IT MATTERS MOST ON ═════════════════════════════
//
// What a closed session HOLDS stays absent for a director it wasn't sent to — its title, who was in
// it, and every motion made inside it but the decisions the board reported. This component renders
// what arrived and says nothing about what did not: no placeholder rows, no counts, no ordinals. See
// `director-minutes-decode.ts`.
//
// ══ THAT A CLOSED SESSION HAPPENED IS PUBLIC (CBG-RC12-READ, D-CBG-25; frame d1-entry) ══════════
//
// The meeting's minutes print a closed session's public entry, so "How the meeting ran" lists EVERY
// session in order from `session_entries`: a closed one by its general purpose, its times and who
// stepped out for a conflict, and its reported decisions under "Reported from the closed session".
// "Open its minutes" leads to its own minutes where this director may open them — straight in when
// they were sent to them, through a recorded step when they weren't (`closed-minutes-opening.tsx`).
// With the signing window shut the backend withholds both facts, and the same words bring the
// confirmation up first. A seat Board's verdict keeps from closed-session material (CBG-SEC2-L4) is
// offered nothing a confirmation couldn't open.
//
// ══ THE SIGNATURE ECHOES THE HASH FROM THE READ THAT OFFERED IT ════════════════════════════════
//
// The attention list names the minutes waiting on this seat AND carries their content address. That
// hash is what the signature echoes — not one fetched at the moment of signing. A signature's whole
// evidentiary value is that it names the exact bytes a human was shown, and an echo obtained
// separately would name bytes nobody read. The act runs through the shared sheet's gate like every
// act (`features/director/director-act.tsx`), so a confirm-and-retry resends that same echo.
//
// ══ "SIGNING AS" (CBG-YR3-ROLES layer 2, `l2-director-sign`) ═══════════════════════════════════
//
// The approved sentence "I sign these minutes." stays as it is (question 4), with the capacity the
// director chooses recorded beside it. The field offers the titles this seat holds NOW (`/me`'s board
// entry), one choice for each set of minutes waiting (a capacity belongs to one signature), and
// starts EMPTY, even for someone with one title, until the board adopts duties (question 9): with
// them, it starts on the title the attention item suggests (layer 3, `suggested_capacity_title_id`,
// the seat's "Signs the minutes" title), which the director can change or take back. Nothing chosen
// sends no capacity. The backend refuses a title the seat no longer holds
// (`board_signature_capacity_not_held`): the field goes back to empty, says so in the director's own
// words, and `/me` is re-read so the choices are this moment's.

import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  firstErrorCode,
  isGivmoError,
  type BoardMeetingSegmentOut,
  type BoardSessionEntryOut,
  type OrgBoardDirectorAttentionOut,
} from '@givmo/api-client';
import { Badge, Card, CardBody, Select, Skeleton, toast } from '@givmo/design-system';
import { Link } from '../../board-routing';
import { useBoardDirectorOperations } from '../../board-runtime';
import { FieldNote } from '../../components/field-note';
import { boardDirectorMeKey, directorBoardFor, useDirectorMe } from '../../director/director-me';
import { DIRECTOR_REFUSALS } from '../../director/director-wire';
import { useDirectorFailureWords } from '../../director/access-refusal';
import { DirectorReadRefusal, useDirectorReadsMayFire } from '../../director/read-gate';
import { displayClockTime, displayInstant } from '../../lib/display';
import { DirectorActRow, DirectorSetupOfferPage, useDirectorAct } from './director-act';
import {
  motionOutcomeLabel,
  motionStatusLabel,
  motionStatusTone,
  presenceSentence,
  recordedQuorumSentence,
  segmentHeading,
  segmentTypeLabel,
  segmentTypeTone,
  tallySentence,
  voteCastLabel,
} from '../in-meeting/in-meeting-decode';
import { closedSessionWords } from '../in-meeting/closed-session-words';
import { capacityFieldValue, capacityOptions, chosenCapacity } from '../roster/officer-roles';
import { officerRolesL2Words as l2 } from '../roster/officer-roles-l2-words';
import { committeeOnlyWords as co } from '../committees/committee-only-words';
import { closedMinutesWords as w } from './closed-minutes-words';
import {
  closedMinutesDestination,
  directorMeetingRecordKey,
  useClosedMinutesOpening,
} from './closed-minutes-opening';
import {
  closedMinutesWay,
  decodeDirectorMeetingRecord,
  isClosedEntry,
  minutesAwaitingDirector,
  steppedOutIsMine,
  type MinutesAwaitingDirector,
} from './director-minutes-decode';

/** The frame's link: brand text, underlined on focus (frame d1-entry). */
const ENTRY_LINK =
  'self-start font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline';

/** A closed entry's line: its times and who stepped out, as the meeting's minutes print them. */
function closedEntryFacts(entry: BoardSessionEntryOut, mySeatId: string | null): string | null {
  const parts: string[] = [];
  if (entry.entered_at) {
    parts.push(
      w.sessionTimes(
        displayClockTime(entry.entered_at),
        entry.exited_at ? displayClockTime(entry.exited_at) : null,
      ),
    );
  }
  if (steppedOutIsMine(entry, mySeatId)) {
    parts.push(w.youSteppedOut);
  } else {
    const names = (entry.stepped_out ?? [])
      .map((person) => person.display_name.trim())
      .filter((name) => name.length > 0);
    if (names.length > 0)
      parts.push(`${closedSessionWords.steppedOutHeading}: ${names.join(', ')}`);
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

/** A session's heading: the full session's own when this director reads it in full, else the entry's. */
function entryHeading(
  entry: BoardSessionEntryOut,
  full: BoardMeetingSegmentOut | undefined,
): string {
  if (full !== undefined) return segmentHeading(full);
  if (isClosedEntry(entry)) return (entry.purpose_label ?? '').trim() || w.noPurpose;
  return (entry.title ?? '').trim() || segmentTypeLabel(entry.segment_type);
}

export interface DirectorMinutesPanelProps {
  orgRef: string;
  meetingId: string;
  /** CBG-FIX90: the meeting's committee, when it is a committee's; its record card says so. */
  committee?: string | null;
}

export function DirectorMinutesPanel({
  orgRef,
  meetingId,
  committee = null,
}: DirectorMinutesPanelProps) {
  const queryClient = useQueryClient();
  const operations = useBoardDirectorOperations();
  const mayFire = useDirectorReadsMayFire(orgRef);
  const meQuery = useDirectorMe();
  // CBG-FIX104: the access refusal in the words of the seat `/me` listed here.
  const failureWords = useDirectorFailureWords();
  // "Signing as": the titles this seat holds now, and the director's choice among them for EACH set of
  // minutes waiting here (empty first) — a capacity belongs to one signature, never to the page.
  const board = directorBoardFor(meQuery.data, orgRef);
  const capacities = board?.titles ?? [];
  // CBG-RC12-READ: this director's own seat (who stepped out reads "You…"), and Board's verdict,
  // which keeps the account from closed-session material however the window stands (CBG-SEC2-L4).
  const mySeatId = board?.board_member_id ?? null;
  const keptFromClosedMaterial = board?.another_account_required === true;
  const opening = useClosedMinutesOpening(orgRef, meetingId);
  const [capacityIds, setCapacityIds] = useState<Readonly<Record<string, string | null>>>({});
  const [capacityErrors, setCapacityErrors] = useState<Readonly<Record<string, string | null>>>({});
  // The minutes the signature in flight is for, so a refused capacity is said at THEIR field.
  const signingRecordId = useRef<string | null>(null);
  const setCapacityFor = (recordId: string, value: string | null, error: string | null = null) => {
    setCapacityIds((ids) => ({ ...ids, [recordId]: value }));
    setCapacityErrors((errors) => ({ ...errors, [recordId]: error }));
  };
  // The choice for these minutes: the director's own once made (a refusal's reset included), else the
  // title the board's duties suggest (layer 3), else none.
  const capacityChoiceFor = (target: MinutesAwaitingDirector): string | null =>
    Object.prototype.hasOwnProperty.call(capacityIds, target.recordId)
      ? (capacityIds[target.recordId] ?? null)
      : target.suggestedCapacityTitleId;

  const recordQuery = useQuery({
    queryKey: directorMeetingRecordKey(orgRef, meetingId),
    // A meeting's record carries its executive sessions, so this read is one the window's floor can
    // refuse under BOTH access modes — not only on a board that asks before anything opens.
    enabled: mayFire,
    // A refusal is a deterministic typed 400, not a blip: retrying it just delays the sheet.
    retry: false,
    queryFn: async () => {
      const { data } = await operations.directorGetBoardDirectorMeetingRecord({
        path: { org_ref: orgRef, meeting_id: meetingId },
        throwOnError: true,
      });
      return decodeDirectorMeetingRecord(data);
    },
  });

  const attentionQuery = useQuery({
    queryKey: ['board-director-attention', orgRef],
    enabled: mayFire,
    retry: false,
    queryFn: async (): Promise<OrgBoardDirectorAttentionOut> => {
      const { data } = await operations.directorGetBoardDirectorAttention({
        path: { org_ref: orgRef },
        throwOnError: true,
      });
      return data;
    },
  });

  const sign = useDirectorAct('minutes_signature', {
    onDone: async () => {
      await queryClient.invalidateQueries({ queryKey: ['board-director-attention', orgRef] });
      await queryClient.invalidateQueries({
        queryKey: directorMeetingRecordKey(orgRef, meetingId),
      });
      toast.success('Signed — thank you.');
    },
    onFailed: (caught) => {
      if (isGivmoError(caught) && firstErrorCode(caught) === DIRECTOR_REFUSALS.capacityNotHeld) {
        // The chosen title left this seat since `/me` was read: back to empty, said at the field of
        // the minutes being signed.
        const recordId = signingRecordId.current;
        if (recordId !== null) setCapacityFor(recordId, null, l2.capacityNotHeld);
        void queryClient.invalidateQueries({ queryKey: boardDirectorMeKey() });
        return;
      }
      if (isGivmoError(caught) && firstErrorCode(caught) === DIRECTOR_REFUSALS.intentChanged) {
        // The sentence above the button changed: the page re-reads it at once, and says what the
        // consent's page says (copy round 2, D-CBG-18), never the backend's "Reload the page".
        void queryClient.invalidateQueries({ queryKey: boardDirectorMeKey() });
        toast.error('The words above changed. Read them, then sign again.');
        return;
      }
      // The backend's own sentence (the access refusal in its approved words, `director-wire.ts`).
      // Two 409s reach here and need different things from a director:
      // the minutes changed since they were read (re-read and sign that), or this seat has already
      // signed (nothing to do). A generic retry message would say neither.
      toast.error(failureWords(caught, 'We couldn’t record that. Please try again.'));
    },
  });

  const beginSign = (target: MinutesAwaitingDirector) => {
    const contentHash = target.contentHash;
    if (contentHash === null) return;
    const path = { org_ref: orgRef, meeting_id: meetingId };
    // The capacity chosen for THESE minutes when Sign was pressed, and only while this seat holds it.
    const capacity = chosenCapacity(capacityChoiceFor(target), capacities);
    signingRecordId.current = target.recordId;
    setCapacityErrors((errors) => ({ ...errors, [target.recordId]: null }));
    sign.begin(
      (intentStatementId) => () =>
        operations.directorSignBoardMinutes({
          path,
          // ⚠ The hash carried by the attention read that offered this signature — see the header.
          body: {
            content_hash: contentHash,
            intent_statement_id: intentStatementId,
            ...(capacity !== null ? { capacity_title_id: capacity } : {}),
          },
          throwOnError: true,
        }),
    );
  };

  if (sign.offering) {
    return <DirectorSetupOfferPage variant="from-sign" onSettled={sign.settleOffer} />;
  }

  // A record this director may not read at all answers exactly as an absent one does, and renders as
  // one. There is no error state here for that reason.
  //
  // ⚠ THE WINDOW'S REFUSAL IS THE ONE ERROR THAT MUST NOT RENDER AS THAT ABSENCE. This director IS
  // entitled to the content — confidentiality ran first server-side and let them through — so
  // vanishing the panel would tell them their board recorded nothing when it recorded plenty. It is
  // also the ordinary case here rather than an edge one: a meeting's record carries its executive
  // sessions, which need the signing window open under BOTH access modes. The read gate answers it.
  //
  // ⚠ ONE RETURN, SO THE READ GATE IS NEVER REMOUNTED. Its retry refetches the refused read, and a
  // read with no data goes back to pending while it does: a gate rendered only under the error would
  // unmount mid-retry, take the sheet with it, and ask again from scratch on the next refusal — a
  // loop between the skeleton and the refusal that never shows the sheet.
  const record = recordQuery.isSuccess ? recordQuery.data : null;
  // ⚠ MUST match THIS meeting, not merely fail to contradict it. Admitting a null `meeting_id`
  // offered the same item on every meeting page a director opened, and signing from the wrong one
  // would POST this meeting's route echoing another record's hash — refused by the backend, but as a
  // "these minutes have changed" message about minutes the director never saw. An item that does not
  // say which meeting it belongs to is not actionable from a meeting page.
  const waiting =
    record === null
      ? []
      : minutesAwaitingDirector(attentionQuery.data?.items).filter(
          (item) => item.meetingId === meetingId,
        );
  // CBG-RC12-READ: every session in order, a closed one as its public entry; an older backend's
  // answer carries none, and reads as before, the sessions this director reads in full.
  const sessionEntries = record?.sessionEntries ?? null;
  const fullSessions = new Map((record?.segments ?? []).map((segment) => [segment.id, segment]));
  // A closed session's reported decisions, where its motions aren't already in `motions` — sent to
  // this director, the session's own motions are listed in full above.
  const reported = (sessionEntries ?? [])
    .filter((entry) => isClosedEntry(entry) && entry.sent_to_you !== true)
    .flatMap((entry) =>
      (entry.reported_decisions ?? []).map((decision, index) => ({
        key: `${entry.id}-${index}`,
        decision,
      })),
    );
  const nothingRecorded =
    record !== null &&
    record.attendance.length === 0 &&
    (sessionEntries ?? record.segments).length === 0 &&
    record.motions.length === 0;

  return (
    <>
      {recordQuery.isPending ? (
        <Skeleton className="h-84 w-full rounded-lg" testId="director-record-loading" />
      ) : null}
      <DirectorReadRefusal reads={[recordQuery]} testId="director-minutes-read-step-up" />

      {waiting.length > 0 ? (
        <Card testId="director-minutes-waiting">
          <CardBody className="flex flex-col gap-12">
            <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
              Minutes waiting for your signature
            </h2>
            {waiting.map((item) => (
              <div
                key={item.recordId}
                className="flex flex-col gap-12"
                data-testid={`director-minutes-waiting-${item.recordId}`}
              >
                <span className="font-sans text-s14 font-w400 tracking-givmo text-text-secondary">
                  {item.title}
                </span>
                {item.contentHash !== null && capacities.length > 0 ? (
                  <div className="flex max-w-[420px] flex-col gap-8">
                    <Select
                      label={l2.signingAs}
                      appearance="saas"
                      placeholder={l2.chooseCapacity}
                      value={capacityFieldValue(capacityChoiceFor(item), capacities)}
                      onValueChange={(next) => setCapacityFor(item.recordId, next)}
                      options={capacityOptions(capacities)}
                      disabled={sign.working}
                      error={(capacityErrors[item.recordId] ?? null) !== null}
                      errorMessage={capacityErrors[item.recordId] ?? undefined}
                      testId={`director-minutes-capacity-${item.recordId}`}
                    />
                    <FieldNote testId={`director-minutes-capacity-note-${item.recordId}`}>
                      {l2.signingAsNote}
                    </FieldNote>
                  </div>
                ) : null}
                {item.contentHash !== null ? (
                  <DirectorActRow
                    state={sign}
                    // The shipped word: only the consent's "Sign this" became "Sign" (approved frame).
                    label="Sign them"
                    onPress={() => beginSign(item)}
                    testIdPrefix={`director-minutes-${item.recordId}`}
                    buttonTestId={`director-minutes-sign-${item.recordId}`}
                  />
                ) : (
                  // No hash on the wire means there is nothing to name in a signature. The
                  // affordance is withdrawn rather than sending an empty echo the backend would
                  // refuse with a message about the minutes having changed.
                  <span className="font-sans text-s13 font-w400 tracking-givmo text-text-tertiary">
                    Not ready to sign yet.
                  </span>
                )}
              </div>
            ))}
          </CardBody>
        </Card>
      ) : null}

      {record !== null ? (
        <Card testId="director-meeting-record">
          <CardBody className="flex flex-col gap-16">
            <div className="flex flex-col gap-4">
              <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
                What was recorded
              </h2>
              <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary">
                {/* CBG-FIX90 (not drawn): a committee's meeting isn't the board's. */}
                {committee !== null
                  ? co.recordLine
                  : 'What your board’s secretary has written down about this meeting.'}
              </p>
            </div>

            {nothingRecorded ? (
              // ⚠ ONE state, deliberately. Nothing here distinguishes "the secretary has recorded
              // nothing yet" from "everything recorded is in sessions you were not part of" — telling
              // those apart is the disclosure the segregation exists to prevent.
              <p
                className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary"
                data-testid="director-record-empty"
              >
                Nothing recorded here yet.
              </p>
            ) : null}

            {/* ⚠ THE DIRECTOR SURFACE RENDERS NO ATTENDANCE FIGURE, AND THAT IS TWO DECISIONS.
              (1) NEVER A COUNT. An earlier draft rendered `attendance.filter(present).length` here —
              exactly the client-side quorum arithmetic `in-meeting-decode.ts`'s header bans, and
              worse on this surface than a style violation: it sat a few lines above the captured
              `seats_present`, so correcting one attendance row made the two contradict each other on
              one screen with nothing telling a reader which was the record.
              (2) AND NOT THE ROWS EITHER, because this principal cannot resolve them. An attendance
              row carries only a soft `board_member_id`, and the director surface has no roster read
              by design — so a list here renders as "Present · Present · Absent" with no names, which
              is noise rather than a record.
              Who was in the room reaches a director as the backend's own SENTENCE instead (CBG-YR3-
              ROLES layer 2, W-14): the minutes renderer's presence line, names and capacities, over
              the meeting's open attendance alone. It is rendered verbatim, never re-derived. */}
            {record.presenceStatement !== null ? (
              <p
                className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-strong"
                data-testid="director-record-presence"
              >
                {record.presenceStatement}
              </p>
            ) : null}

            {sessionEntries === null && record.segments.length > 0 ? (
              <div className="flex flex-col gap-6" data-testid="director-record-sessions">
                <span className="font-sans text-s12 font-w600 tracking-givmo text-text-secondary">
                  How the meeting ran
                </span>
                <ul className="flex flex-col gap-4">
                  {record.segments.map((segment) => (
                    <li key={segment.id} className="flex flex-wrap items-center gap-8">
                      <span className="font-sans text-s13 font-w400 tracking-givmo text-text-secondary">
                        {segmentHeading(segment)}
                      </span>
                      <Badge tone={segmentTypeTone(segment.segment_type)}>
                        {segmentTypeLabel(segment.segment_type)}
                      </Badge>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {sessionEntries !== null && sessionEntries.length > 0 ? (
              // CBG-RC12-READ (frame d1-entry): every session in order, a closed one as its public
              // entry. Never an ordinal: the order is the list's own.
              <div className="flex flex-col gap-6" data-testid="director-record-sessions">
                <span className="font-sans text-s12 font-w600 tracking-givmo text-text-secondary">
                  How the meeting ran
                </span>
                <ul className="flex flex-col gap-8">
                  {sessionEntries.map((entry) => {
                    const heading = entryHeading(entry, fullSessions.get(entry.id));
                    if (!isClosedEntry(entry)) {
                      return (
                        <li
                          key={entry.id}
                          className="flex flex-wrap items-center gap-8"
                          data-testid={`director-session-${entry.id}`}
                        >
                          <span className="font-sans text-s13 font-w400 tracking-givmo text-text-secondary">
                            {heading}
                          </span>
                          <Badge tone={segmentTypeTone(entry.segment_type)}>
                            {segmentTypeLabel(entry.segment_type)}
                          </Badge>
                        </li>
                      );
                    }
                    const facts = closedEntryFacts(entry, mySeatId);
                    const way = closedMinutesWay(entry);
                    const asking = opening.asking === entry.id;
                    return (
                      <li
                        key={entry.id}
                        className="flex flex-col gap-4"
                        data-testid={`director-session-${entry.id}`}
                      >
                        <div className="flex flex-wrap items-center gap-8">
                          <span className="font-sans text-s13 font-w400 tracking-givmo text-text-secondary">
                            {heading}
                          </span>
                          <Badge tone={segmentTypeTone(entry.segment_type)}>
                            {segmentTypeLabel(entry.segment_type)}
                          </Badge>
                        </div>
                        {facts !== null ? (
                          <span
                            className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary"
                            data-testid={`director-session-facts-${entry.id}`}
                          >
                            {facts}
                          </span>
                        ) : null}
                        {way.kind === 'open' ? (
                          <Link
                            {...closedMinutesDestination(orgRef, meetingId, entry.id, way)}
                            className={ENTRY_LINK}
                            data-testid={`director-session-open-${entry.id}`}
                          >
                            {w.openItsMinutes}
                          </Link>
                        ) : null}
                        {way.kind === 'ask' && !keptFromClosedMaterial ? (
                          // The window is shut: the same words bring the confirmation up first.
                          <button
                            type="button"
                            className={`${ENTRY_LINK} disabled:cursor-default disabled:text-text-tertiary`}
                            onClick={() => void opening.ask(entry.id)}
                            disabled={opening.asking !== null}
                            aria-busy={asking && !opening.prompting ? true : undefined}
                            data-testid={`director-session-ask-${entry.id}`}
                          >
                            {w.openItsMinutes}
                          </button>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}

            {record.motions.length > 0 ? (
              <div className="flex flex-col gap-8" data-testid="director-record-motions">
                <span className="font-sans text-s12 font-w600 tracking-givmo text-text-secondary">
                  What the board moved
                </span>
                <ul className="flex flex-col gap-10">
                  {record.motions.map((motion) => {
                    const presence = presenceSentence(motion.quorum);
                    const recorded = recordedQuorumSentence(motion.quorum);
                    const tally = tallySentence(motion);
                    const outcome = motionOutcomeLabel(motion.outcome);
                    return (
                      <li
                        key={motion.id}
                        className="flex flex-col gap-4 rounded-lg border border-border-subtle p-12"
                        data-testid={`director-motion-${motion.id}`}
                      >
                        <div className="flex flex-wrap items-center gap-8">
                          <Badge tone={motionStatusTone(motion.status)}>
                            {motionStatusLabel(motion.status)}
                          </Badge>
                          {outcome !== null ? <Badge tone="neutral">{outcome}</Badge> : null}
                        </div>
                        <p className="whitespace-pre-wrap font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-strong">
                          {motion.motion_text}
                        </p>
                        {tally !== null ? (
                          <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
                            {tally}
                          </span>
                        ) : null}
                        {presence !== null || recorded !== null ? (
                          // The same figures the officer sees, rendered by the same helpers. A recused
                          // seat is still in both numbers.
                          <span
                            className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary"
                            data-testid={`director-motion-quorum-${motion.id}`}
                          >
                            {[presence, recorded].filter((part) => part !== null).join(' · ')}
                          </span>
                        ) : null}
                        {motion.voted_at ? (
                          <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
                            Voted {displayInstant(motion.voted_at)}
                          </span>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}

            {reported.length > 0 ? (
              // CBG-RC12-READ (frame d1-entry): the decisions a closed session reports, as the
              // meeting's minutes report them — wording, the recorded counts, the outcome and who
              // was recused. Never a director's own vote.
              <div className="flex flex-col gap-8" data-testid="director-record-reported">
                <span className="font-sans text-s12 font-w600 tracking-givmo text-text-secondary">
                  {w.reportedHeading}
                </span>
                <ul className="flex flex-col gap-10">
                  {reported.map(({ key, decision }) => {
                    const outcome = motionOutcomeLabel(decision.outcome);
                    const counted =
                      typeof decision.votes_for === 'number' &&
                      typeof decision.votes_against === 'number' &&
                      typeof decision.votes_abstaining === 'number';
                    const recused = (decision.recused ?? [])
                      .map((person) => person.display_name.trim())
                      .filter((name) => name.length > 0);
                    const line = [
                      counted
                        ? w.recordedVote(
                            decision.votes_for as number,
                            decision.votes_against as number,
                            decision.votes_abstaining as number,
                          )
                        : null,
                      recused.length > 0 ? w.recordedAsRecused(recused.join(', ')) : null,
                    ]
                      .filter((part): part is string => part !== null)
                      .join(' · ');
                    return (
                      <li
                        key={key}
                        className="flex flex-col gap-4 rounded-lg border border-border-subtle p-12"
                        data-testid={`director-reported-${key}`}
                      >
                        <div className="flex flex-wrap items-center gap-8">
                          <Badge tone={motionStatusTone('voted')}>
                            {motionStatusLabel('voted')}
                          </Badge>
                          {outcome !== null ? <Badge tone="neutral">{outcome}</Badge> : null}
                        </div>
                        <p className="whitespace-pre-wrap font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-strong">
                          {decision.motion_text}
                        </p>
                        {line.length > 0 ? (
                          <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
                            {line}
                          </span>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}

            {record.myVotes.length > 0 || record.myRecusals.length > 0 ? (
              <div className="flex flex-col gap-6" data-testid="director-record-mine">
                <span className="font-sans text-s12 font-w600 tracking-givmo text-text-secondary">
                  What the record says about you
                </span>
                {record.myVotes.map((vote, index) => (
                  <span
                    key={`vote-${index}`}
                    className="font-sans text-s13 font-w400 tracking-givmo text-text-secondary"
                  >
                    You voted {voteCastLabel(vote.vote_cast).toLowerCase()}
                  </span>
                ))}
                {record.myRecusals.map((recusal, index) => (
                  <span
                    key={`recusal-${index}`}
                    className="font-sans text-s13 font-w400 tracking-givmo text-text-secondary"
                  >
                    You were recused
                    {recusal.basis_note ? ` — ${recusal.basis_note}` : ''}
                  </span>
                ))}
              </div>
            ) : null}
          </CardBody>
        </Card>
      ) : null}

      {sign.dialog}
      {opening.dialog}
    </>
  );
}
